import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check, Clock, ImagePlus, Loader2, Lock, Sparkles, Trash2, Zap } from 'lucide-react';
import { fetchThemePresets, saveTheme, unlockTheme, uploadThemeBackground, markThemeSetupSeen } from '@/lib/childProfiles';
import { SetupStepper } from '@/components/study/SetupStepper';

/**
 * Per-profile study theme. Background photos are pinned at the server's fixed
 * opacity so a parent cannot accidentally make the study text unreadable.
 * With `setup` it becomes the last (skippable) step of first-run onboarding.
 */
export function ThemeDialog({ open, onOpenChange, target, onSaved, setup = false, step = 3, totalSteps = 5, onDone, onBack }) {
  const [presets, setPresets] = useState([]);
  const [xp, setXp] = useState(0);
  const [opacity, setOpacity] = useState(0.4);
  const [selected, setSelected] = useState(target?.theme?.preset || 'none');
  const [hasBg, setHasBg] = useState(!!target?.theme?.background_path);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const profileId = target?.kind === 'self' ? null : target?.profile_id || null;

  useEffect(() => {
    if (!open) return;
    setSelected(target?.theme?.preset || 'none');
    setHasBg(!!target?.theme?.background_path);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target]);

  const reload = () => fetchThemePresets()
    .then(d => { setPresets(d.presets || []); setXp(d.xp_balance || 0); setOpacity(d.background_opacity ?? 0.4); })
    .catch(() => setPresets([]));

  const unlock = async (p) => {
    setBusy(true);
    try {
      const res = await unlockTheme(p.key);
      await reload();
      if (res.status === 'unlocked') {
        toast.success(`${res.label} unlocked! ${res.xp_balance} Veda Coins left`);
        await pick(p.key);
      }
    } catch (e) { toast.error(e?.message || 'Could not unlock that theme'); }
    setBusy(false);
  };

  const pick = async (key) => {
    setSelected(key); setBusy(true);
    try {
      const res = await saveTheme(key, profileId);
      onSaved?.(res.theme);
      toast.success(`${res.theme.label} theme applied`);
    } catch (e) { toast.error(e?.message || 'Could not apply that theme'); }
    setBusy(false);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadThemeBackground(file, profileId);
      setHasBg(true); onSaved?.(res.theme);
      toast.success('Background added');
    } catch (err) { toast.error(err?.message || 'Could not upload that image'); }
    setBusy(false);
    e.target.value = '';
  };

  const clearBg = async () => {
    setBusy(true);
    try {
      const res = await saveTheme(selected, profileId, true);
      setHasBg(false); onSaved?.(res.theme);
      toast.success('Background removed');
    } catch (e) { toast.error(e?.message || 'Could not remove the background'); }
    setBusy(false);
  };

  // First-run step 3: either way we only ever ask once.
  const finishSetup = async (applyChoice) => {
    setBusy(true);
    try {
      if (applyChoice) {
        const res = await saveTheme(selected, profileId);
        onSaved?.(res.theme);
      } else {
        await markThemeSetupSeen();
      }
    } catch (e) { toast.error(e?.message || 'Could not save your theme'); }
    setBusy(false);
    onDone?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg rounded-3xl p-0 gap-0 flex flex-col max-h-[88dvh] overflow-hidden"
        hideClose={setup}
        onEscapeKeyDown={e => { if (setup) e.preventDefault(); }}
        onPointerDownOutside={e => { if (setup) e.preventDefault(); }}
        onInteractOutside={e => { if (setup) e.preventDefault(); }}
        data-testid="theme-dialog">
        {/* Header stays put — the scene list is long and the title used to
            scroll off the top of the screen with no way back. */}
        <div className="flex-none px-5 pt-5 pb-3 border-b border-slate-100">
        {setup && <div className="mb-2"><SetupStepper step={step} total={totalSteps} /></div>}
        <DialogTitle className="font-heading text-xl font-bold text-slate-900">
          {setup ? 'Pick your study theme' : `Study theme${target?.name ? ` — ${target.name}` : ''}`}
        </DialogTitle>
        <DialogDescription className="text-xs text-slate-500 font-body">
          Pick a world to study in — each child keeps their own. Or add your own photo,
          which we hold at {Math.round(opacity * 100)}% so studying always stays readable.
        </DialogDescription>

        {xp > 0 && (
          <p className="mt-2 text-[11px] font-body text-slate-500 inline-flex items-center gap-1" data-testid="theme-xp-balance">
            <Zap className="w-3 h-3 text-violet-500" /> You have <span className="font-heading text-violet-700">{xp} Veda Coins</span> to spend
          </p>
        )}
        {setup && (
          <p className="mt-2 text-[11px] font-body text-slate-500" data-testid="theme-setup-aspiration">
            Locked scenes stay visible on purpose — earn them with Veda Coins as you study.
            You can change your theme any time from your profile menu.
          </p>
        )}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 pb-4" data-testid="theme-scroll-area">

        {[
          { group: 'seasonal', title: 'Here for a limited time · Veda Coins', icon: Clock },
          { group: 'always', title: 'Always yours', icon: null },
          { group: 'earned', title: 'Earn with Veda Coins', icon: Sparkles },
          { group: 'legend', title: 'Legend scenes · Veda Coins', icon: Sparkles },
        ].map(({ group, title, icon: Icon }) => {
          const rows = presets.filter(p => p.group === group);
          if (!rows.length) return null;
          return (
            <div key={group} className="mt-3">
              <p className="text-[10px] font-heading uppercase tracking-widest text-slate-400 flex items-center gap-1">
                {Icon && <Icon className="w-3 h-3" />} {title}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-1.5">
                {rows.map(p => {
                  const blocked = !p.usable;
                  return (
                    <button key={p.key} data-testid={`theme-preset-${p.key}`} disabled={busy}
                      onClick={() => {
                        // Festive, earned and legend scenes are all bought with
                        // coins; festive ones only while their window is open.
                        if (p.cost && !p.owned) {
                          if (p.group === 'seasonal' && !p.in_season) {
                            return toast.message(`${p.label} comes back around ${p.occasion} (${p.window})`);
                          }
                          return unlock(p);
                        }
                        if (!blocked) return pick(p.key);
                        // Say WHY rather than doing nothing when tapped.
                        return toast.message(
                          p.group === 'seasonal'
                            ? `${p.label} comes back around ${p.occasion} (${p.window})`
                            : `${p.label} needs unlocking first`,
                        );
                      }}
                      className={`group relative rounded-2xl overflow-hidden text-left ring-2 transition-all hover:-translate-y-1 hover:shadow-xl disabled:opacity-60 ${selected === p.key ? 'ring-indigo-500 shadow-lg' : 'ring-transparent hover:ring-white/60'}`}>
                      {p.image ? (
                        <img src={p.image} alt={p.label} loading="lazy"
                          className={`w-full h-24 object-cover transition-transform duration-500 group-hover:scale-105 ${blocked ? 'grayscale-[0.5] brightness-75' : ''}`}
                          style={{ background: `linear-gradient(135deg,${p.swatch[0]},${p.swatch[1]})` }} />
                      ) : (
                        <div className="w-full h-24 bg-white border border-slate-200 flex items-center justify-center"
                          data-testid="theme-preset-none-tile">
                          <span className="text-[11px] font-heading text-slate-400">Plain</span>
                        </div>
                      )}
                      <span className={`absolute inset-x-0 bottom-0 p-2 ${p.image ? 'bg-gradient-to-t from-black/85 via-black/50 to-transparent' : 'bg-white/90'}`}>
                        <span className={`block font-heading text-[12px] leading-tight ${p.image ? 'text-white' : 'text-slate-900'}`}>{p.label}</span>
                        <span className={`block text-[10px] font-body leading-tight ${p.image ? 'text-white/75' : 'text-slate-500'}`}>{p.hint}</span>
                      </span>

                      {/* Urgency: a season you can lose */}
                      {p.in_season && (
                        <span className="absolute top-1.5 left-1.5 text-[9px] font-heading uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-rose-500 text-white shadow"
                          data-testid={`theme-season-badge-${p.key}`}>
                          {p.days_left === 0 ? 'last day' : `${p.days_left}d left`}
                        </span>
                      )}
                      {p.group === 'seasonal' && !p.in_season && (
                        <span className="absolute top-1.5 left-1.5 text-[9px] font-body px-1.5 py-0.5 rounded-full bg-slate-900/75 text-white/90">
                          {p.window}
                        </span>
                      )}
                      {p.cost && !p.owned && (
                        <span className="absolute top-1.5 left-1.5 text-[9px] font-heading px-1.5 py-0.5 rounded-full bg-violet-600 text-white shadow inline-flex items-center gap-1"
                          data-testid={`theme-cost-${p.key}`}>
                          <Lock className="w-2.5 h-2.5" /> {p.cost} Veda Coins
                        </span>
                      )}
                      {p.free_for_admin && (
                        <span className="absolute top-1.5 left-1.5 text-[9px] font-heading px-1.5 py-0.5 rounded-full bg-emerald-600 text-white shadow"
                          data-testid={`theme-admin-free-${p.key}`}>
                          Free for you
                        </span>
                      )}
                      {selected === p.key && (
                        <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-indigo-500 text-white flex items-center justify-center shadow-md">
                          <Check className="w-3 h-3" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        <div className="mt-4 rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-3">
          <p className="text-[10px] font-heading uppercase tracking-widest text-slate-400">Own photo</p>
          <p className="text-[11px] font-body text-slate-600 mt-1">
            Add a favourite picture as the background — JPG, PNG or WebP, up to 5 MB.
          </p>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onFile}
            data-testid="theme-bg-input" />
          <div className="flex items-center gap-2 mt-2">
            <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy}
              data-testid="theme-bg-upload" onClick={() => fileRef.current?.click()}>
              {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ImagePlus className="w-3 h-3 mr-1" />}
              {hasBg ? 'Replace photo' : 'Choose photo'}
            </Button>
            {hasBg && (
              <Button size="sm" variant="ghost" className="h-7 text-[11px] text-rose-600" disabled={busy}
                data-testid="theme-bg-clear" onClick={clearBg}>
                <Trash2 className="w-3 h-3 mr-1" /> Remove
              </Button>
            )}
          </div>
        </div>

        </div>

        <div className="flex-none flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-100 bg-white/95">
          {setup ? (
            <>
              {onBack ? (
                <button type="button" data-testid="theme-back-btn" onClick={onBack}
                  className="text-[12px] font-heading text-slate-500 hover:text-slate-800">← Back</button>
              ) : <span />}
              <div className="flex items-center gap-3">
                <button type="button" data-testid="theme-setup-later" disabled={busy}
                  onClick={() => finishSetup(false)}
                  className="text-[12px] font-body text-slate-500 hover:text-slate-800 hover:underline disabled:opacity-50">
                  Set up later
                </button>
                <Button size="sm" disabled={busy} data-testid="theme-setup-done" onClick={() => finishSetup(true)}>
                  Next
                </Button>
              </div>
            </>
          ) : (
            <Button variant="outline" size="sm" className="ml-auto" onClick={() => onOpenChange(false)} data-testid="theme-close">Done</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ThemeDialog;
